import { asArray, asOptionalString, asRecord, asString } from '../../dto/common';
import { formatUnit } from '../../utils/units';

/**
 * Operator-facing "why is this unavailable" for PZ/WZ documents.
 *
 * The backend computes the rule once (services/warehouseBlockedActionsService.js)
 * and ships it on every document GET as `blockedActions`; the same code and
 * `relatedDocuments` come back on a rejected mutation. This module is the
 * single place that turns either into a Polish sentence, so the tooltip on a
 * disabled button, the alert on the document card and the toast after a
 * rejected call read identically.
 *
 * Wording lives in `pz:error.*` / `wz:error.*` (reason, shared with reactive
 * toasts) plus `pz:blocked.remedy.*` / `wz:blocked.remedy.*` (what to do
 * instead). Related documents name only document numbers, batches and
 * physical quantities — the backend never puts money in them.
 */
export type WarehouseRelatedDocumentType = 'WZ' | 'PZ' | 'ORDER' | 'ITEM' | 'TRANSPORT' | 'INVOICE';

export interface WarehouseRelatedDocument {
  type: WarehouseRelatedDocumentType;
  id: string | null;
  number: string | null;
  batchNumber: string | null;
  quantity: number | null;
  unit: string | null;
}

export interface WarehouseBlockedAction {
  action: string;
  code: string;
  relatedDocuments: WarehouseRelatedDocument[];
  /** Advisory only — the action still works (e.g. DELIVERED without a signed WZ). */
  warning?: boolean;
}

/**
 * i18n namespace that carries the `blocked.*` keys. Customer orders and
 * transports joined the contract on 2026-09-18 (owner decision) and reuse
 * this module unchanged.
 */
export type WarehouseDocumentNamespace = 'pz' | 'wz' | 'orders' | 'transport';

const RELATED_TYPES: WarehouseRelatedDocumentType[] = ['WZ', 'PZ', 'ORDER', 'ITEM', 'TRANSPORT', 'INVOICE'];

export function normalizeRelatedDocuments(raw: unknown): WarehouseRelatedDocument[] {
  return asArray(raw, (item) => {
    const row = asRecord(item);
    const type = asOptionalString(row?.type);
    const quantity = Number(row?.quantity);
    return {
      type: type && RELATED_TYPES.includes(type as WarehouseRelatedDocumentType)
        ? (type as WarehouseRelatedDocumentType)
        : 'ITEM',
      id: asOptionalString(row?.id) ?? null,
      number: asOptionalString(row?.number) ?? null,
      batchNumber: asOptionalString(row?.batchNumber) ?? null,
      quantity: row?.quantity == null || !Number.isFinite(quantity) ? null : quantity,
      unit: asOptionalString(row?.unit) ?? null,
    };
  });
}

/** Missing or malformed payload means "nothing known", never "nothing blocked". */
export function normalizeBlockedActions(raw: unknown): WarehouseBlockedAction[] {
  return asArray(raw, (item) => {
    const row = asRecord(item);
    return {
      action: asString(row?.action),
      code: asString(row?.code),
      relatedDocuments: normalizeRelatedDocuments(row?.relatedDocuments),
      ...(row?.warning === true ? { warning: true } : {}),
    };
  }).filter((row) => row.action && row.code);
}

export function findBlockedAction(
  list: WarehouseBlockedAction[] | undefined,
  action: string,
): WarehouseBlockedAction | undefined {
  return (list || []).find((row) => row.action === action);
}

const quantityFormatter = new Intl.NumberFormat('pl-PL', { maximumFractionDigits: 3 });

function formatQuantity(quantity: number | null, unit: string | null): string {
  if (quantity == null) return '';
  return ` (${quantityFormatter.format(quantity)} ${formatUnit(unit)})`;
}

/**
 * One entry per document, not per batch: batch numbers carry a technical
 * suffix that means nothing to an operator, so rows of the same document and
 * unit are merged and their quantities summed.
 */
function groupRelatedDocuments(docs: WarehouseRelatedDocument[]): WarehouseRelatedDocument[] {
  const groups = new Map<string, WarehouseRelatedDocument>();
  docs.forEach((doc) => {
    const key = `${doc.type}|${doc.number ?? doc.id ?? ''}|${doc.unit ?? ''}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { ...doc });
      return;
    }
    existing.quantity = existing.quantity == null || doc.quantity == null
      ? existing.quantity ?? doc.quantity
      : existing.quantity + doc.quantity;
  });
  return Array.from(groups.values());
}

/**
 * "towar z PZ/02/09/2026 (100 szt.)" — one clause per related document,
 * joined with commas. Empty string when nothing is attached, so callers can
 * concatenate without a guard. The `quantity` param already carries its
 * parentheses (or is empty), so templates read `{{number}}{{quantity}}`.
 */
export function formatRelatedDocuments(
  docs: WarehouseRelatedDocument[] | undefined,
  t: any,
  ns: WarehouseDocumentNamespace,
): string {
  const clauses = groupRelatedDocuments(docs || []).map((doc) => {
    const params = {
      number: doc.number || '—',
      quantity: formatQuantity(doc.quantity, doc.unit),
    };
    switch (doc.type) {
      case 'WZ':
        return t(`${ns}:blocked.related.issuedOnWZ`, params);
      case 'ORDER':
        return t(`${ns}:blocked.related.reservedForOrder`, params);
      case 'PZ':
        return t(`${ns}:blocked.related.consumedFromPZ`, params);
      case 'TRANSPORT':
        return t(`${ns}:blocked.related.transport`, params);
      case 'INVOICE':
        return t(`${ns}:blocked.related.invoiced`, params);
      default:
        return t(`${ns}:blocked.related.item`, params);
    }
  }).filter(Boolean);
  return clauses.join(', ');
}

export interface BlockedActionMessageInput {
  code: string;
  relatedDocuments?: WarehouseRelatedDocument[];
}

/**
 * Codes whose remedy sends the operator to the document's "Edytuj" (correction).
 * When the edit action is itself blocked, that advice is wrong — the edit
 * line carries the real way out — so the remedy is dropped.
 */
const REMEDY_VIA_EDIT = new Set([
  'WZ_CANCEL_BLOCKED_RELEASED',
  'WZ_DELETE_BLOCKED_STATUS',
  'PZ_DELETE_BLOCKED_STATUS',
]);

export interface BlockedActionMessageOptions {
  /** All blocked actions of the document; lets the remedy respect a blocked edit. */
  siblings?: WarehouseBlockedAction[];
  /** false when an earlier line already named the same documents. */
  includeRelated?: boolean;
}

function remedyApplies(code: string, siblings: WarehouseBlockedAction[] | undefined): boolean {
  if (!REMEDY_VIA_EDIT.has(code)) return true;
  return !findBlockedAction(siblings, 'edit');
}

/**
 * Full operator sentence: reason, then the documents behind it, then what to
 * do instead. `reasonKeys` is the namespace's code→key map (exported by
 * `pzErrorMessages.ts` / `wzErrorMessages.ts`); an unmapped code falls back to
 * the generic unavailable sentence rather than leaking the code.
 */
export function getBlockedActionMessage(
  blocked: BlockedActionMessageInput,
  t: any,
  ns: WarehouseDocumentNamespace,
  reasonKeys: Record<string, string>,
  options: BlockedActionMessageOptions = {},
): string {
  const reasonKey = reasonKeys[blocked.code];
  const reason = reasonKey ? t(reasonKey) : t(`${ns}:blocked.unavailable`);
  const related = options.includeRelated === false
    ? ''
    : formatRelatedDocuments(blocked.relatedDocuments, t, ns);
  const remedyPath = `blocked.remedy.${blocked.code}`;
  const remedy = remedyApplies(blocked.code, options.siblings) ? t(`${ns}:${remedyPath}`) : '';
  const parts = [reason];
  if (related) parts.push(t(`${ns}:blocked.related.prefix`, { list: related }));
  // i18next echoes a missing key without its namespace, so match the path.
  if (typeof remedy === 'string' && remedy && !remedy.endsWith(remedyPath)) parts.push(remedy);
  return parts.join(' ');
}

/**
 * Lines for the card alert: same sentences as the tooltips, but documents
 * already named by an earlier line are not repeated.
 */
export function getBlockedActionLines(
  list: WarehouseBlockedAction[],
  t: any,
  ns: WarehouseDocumentNamespace,
  reasonKeys: Record<string, string>,
): Array<{ blocked: WarehouseBlockedAction; message: string }> {
  const seenRelated = new Set<string>();
  return list.map((blocked) => {
    const related = formatRelatedDocuments(blocked.relatedDocuments, t, ns);
    const includeRelated = Boolean(related) && !seenRelated.has(related);
    if (related) seenRelated.add(related);
    return {
      blocked,
      message: getBlockedActionMessage(blocked, t, ns, reasonKeys, { siblings: list, includeRelated }),
    };
  });
}
